import { getApiBaseUrl } from "../config/appSettings";

export const CMS_SESSION_KEY = "ainsoft_cms_session";

export function getStoredToken() {
  try {
    const raw = localStorage.getItem(CMS_SESSION_KEY);
    if (!raw) return null;
    const s = JSON.parse(raw);
    if (s.expiresAt && new Date(s.expiresAt) < new Date()) {
      localStorage.removeItem(CMS_SESSION_KEY);
      return null;
    }
    return s.token ?? null;
  } catch {
    return null;
  }
}

function buildApiUrl(path) {
  const apiBaseUrl = String(getApiBaseUrl() ?? "").replace(/\/+$/, "");
  const normalizedPath = String(path ?? "").startsWith("/") ? String(path) : `/${String(path ?? "")}`;
  return `${apiBaseUrl}${normalizedPath}`;
}

function authHeaders(path) {
  if (path.startsWith("/api/Authentication/login")) {
    return {};
  }
  const token = getStoredToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

function onUnauthorized() {
  localStorage.removeItem(CMS_SESSION_KEY);
  if (window.location.pathname !== "/login") {
    window.location.assign("/login");
  }
}

/**
 * Server invalidates the session; always clears local storage after the call.
 * Does not trigger onUnauthorized for 401 to avoid a double redirect.
 */
export async function apiLogout() {
  const url = buildApiUrl("/api/Authentication/logout");
  const token = getStoredToken();
  try {
    const res = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {})
      },
      body: JSON.stringify({})
    });
    try {
      localStorage.removeItem(CMS_SESSION_KEY);
    } catch {
      /* ignore */
    }
    if (res.ok) {
      return { ok: true, data: null, error: null };
    }
    if (res.status === 401) {
      return { ok: true, data: null, error: null };
    }
    const text = await res.text();
    return { ok: false, data: null, error: text || res.statusText };
  } catch (err) {
    try {
      localStorage.removeItem(CMS_SESSION_KEY);
    } catch {
      /* ignore */
    }
    const msg = err instanceof Error ? err.message : "Network error";
    return { ok: false, data: null, error: msg };
  }
}

export async function apiGet(path) {
  const url = buildApiUrl(path);
  try {
    const res = await fetch(url, {
      headers: {
        Accept: "application/json",
        ...authHeaders(path)
      }
    });
    if (res.status === 401) {
      onUnauthorized();
      return { ok: false, data: null, error: "Unauthorized" };
    }
    if (!res.ok) {
      const text = await res.text();
      return { ok: false, data: null, error: text || res.statusText };
    }
    const data = await res.json();
    return { ok: true, data };
  } catch (err) {
    const msg = err instanceof Error ? err.message : "Network error";
    return { ok: false, data: null, error: msg };
  }
}

export async function apiPost(path, body) {
  const url = buildApiUrl(path);
  try {
    const res = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
        ...authHeaders(path)
      },
      body: JSON.stringify(body)
    });
    if (res.status === 401) {
      onUnauthorized();
      return { ok: false, data: null, error: "Unauthorized" };
    }
    if (!res.ok) {
      const text = await res.text();
      return { ok: false, data: null, error: text || res.statusText };
    }
    const data = await res.json();
    return { ok: true, data };
  } catch (err) {
    const msg = err instanceof Error ? err.message : "Network error";
    return { ok: false, data: null, error: msg };
  }
}

export async function apiPut(path, body) {
  const url = buildApiUrl(path);
  try {
    const res = await fetch(url, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
        ...authHeaders(path)
      },
      body: JSON.stringify(body)
    });
    if (res.status === 401) {
      onUnauthorized();
      return { ok: false, data: null, error: "Unauthorized" };
    }
    if (!res.ok) {
      const text = await res.text();
      return { ok: false, data: null, error: text || res.statusText };
    }
    const data = await res.json();
    return { ok: true, data };
  } catch (err) {
    const msg = err instanceof Error ? err.message : "Network error";
    return { ok: false, data: null, error: msg };
  }
}

export async function apiDelete(path) {
  const url = buildApiUrl(path);
  try {
    const res = await fetch(url, {
      method: "DELETE",
      headers: {
        ...authHeaders(path)
      }
    });
    if (res.status === 401) {
      onUnauthorized();
      return { ok: false, data: null, error: "Unauthorized" };
    }
    if (!res.ok) {
      const text = await res.text();
      return { ok: false, data: null, error: text || res.statusText };
    }
    return { ok: true, data: null };
  } catch (err) {
    const msg = err instanceof Error ? err.message : "Network error";
    return { ok: false, data: null, error: msg };
  }
}

export async function apiUploadImage(path, file, extraFields = {}) {
  const url = buildApiUrl(path);
  try {
    const formData = new FormData();
    formData.append("file", file);
    for (const [key, value] of Object.entries(extraFields || {})) {
      if (value === undefined || value === null) continue;
      formData.append(key, String(value));
    }
    const res = await fetch(url, {
      method: "POST",
      body: formData,
      headers: authHeaders(path)
    });
    if (res.status === 401) {
      onUnauthorized();
      return { ok: false, data: null, error: "Unauthorized" };
    }
    if (!res.ok) {
      const text = await res.text();
      return { ok: false, data: null, error: text || res.statusText };
    }
    const data = await res.json();
    return { ok: true, data };
  } catch (err) {
    const msg = err instanceof Error ? err.message : "Network error";
    return { ok: false, data: null, error: msg };
  }
}

export async function apiUploadPdf(path, file, extraFields = {}) {
  return apiUploadImage(path, file, extraFields);
}
